'use client'

import { useMemo } from 'react'

import { cn, normalize } from '@/_lib/utils'

import { AStepSelectAmbulanceListItem } from './a-step-select-ambulance-list-item'

import type { IAmbulanceCategory } from '@/_types'

/**
 * Reorders `items` so that they flow column-by-column (column-major) when placed in a
 * row-major CSS grid with `cols` columns. The first column gets items 0…R-1, the second
 * gets R…2R-1, etc., where R = ⌈n / cols⌉.
 *
 * When `items.length` is not divisible by `cols`, the last row has empty grid cells; those
 * are represented as `null` so the returned array length is always `rows * cols` and DOM
 * order matches visual grid positions (no accidental row-major compaction).
 *
 * @example reorderColumnMajor(['A','B','C','D','E','F'], 3) → ['A','C','E','B','D','F']
 */
function reorderColumnMajor<T>(items: T[], cols: number): (T | null)[] {
	if (cols <= 1) return [...items]
	const rows = Math.ceil(items.length / cols)
	const result: (T | null)[] = []
	for (let r = 0; r < rows; r++) {
		for (let c = 0; c < cols; c++) {
			const idx = c * rows + r
			result.push(idx < items.length ? items[idx] : null)
		}
	}
	return result
}

export type AStepSelectAmbulanceGroupGridProps = {
	/** Filtered leaf rows for one subgroup under the active top-level category. */
	visibleRows: IAmbulanceCategory[]
	/** Invoked when the user activates a row that has an `ambulance` payload. */
	onRowSelect: (item: IAmbulanceCategory) => void
}

/**
 * Responsive multi-column list of ambulance rows with decorative vertical guides between columns.
 * Items always flow alphabetically top-to-bottom within each column (column-major order).
 *
 * @param visibleRows - Rows to render; empty arrays produce an empty list (parent usually skips rendering).
 * @param onRowSelect - Forwarded to each {@link AStepSelectAmbulanceListItem}.
 * @returns Relative wrapper, column guide overlays, and breakpoint-specific grid `<ul>` elements.
 */
export function AStepSelectAmbulanceGroupGrid({
	visibleRows,
	onRowSelect
}: AStepSelectAmbulanceGroupGridProps) {
	const rows2 = useMemo(() => reorderColumnMajor(visibleRows, 2), [visibleRows])
	const rows3 = useMemo(() => reorderColumnMajor(visibleRows, 3), [visibleRows])

	return (
		<div className='relative mt-2'>
			{/* md–lg: one uninterrupted vertical line between two columns */}
			<div
				aria-hidden
				className='pointer-events-none absolute top-0 bottom-0 z-1 hidden w-px -translate-x-1/2 bg-medante-purple-dark/20 md:block lg:hidden'
				style={{ left: '50%' }}
			/>
			{/* lg: two lines between three columns; gap-x-8 = 2rem, two gaps = 4rem */}
			<div
				aria-hidden
				className='pointer-events-none absolute top-0 bottom-0 z-1 hidden w-px -translate-x-1/2 bg-medante-purple-dark/20 lg:block'
				style={{ left: 'calc((100% - 4rem) / 3 + 1rem)' }}
			/>
			<div
				aria-hidden
				className='pointer-events-none absolute top-0 bottom-0 z-1 hidden w-px -translate-x-1/2 bg-medante-purple-dark/20 lg:block'
				style={{ left: 'calc(2 * (100% - 4rem) / 3 + 3rem)' }}
			/>

			{/* Mobile: 1 column, original order */}
			<ul className='relative z-0 grid grid-cols-1 md:hidden'>
				{visibleRows.map((item, index) => (
					<AStepSelectAmbulanceListItem
						key={item.id}
						item={item}
						onSelect={onRowSelect}
						className={cn(index === visibleRows.length - 1 && 'border-b-0!')}
					/>
				))}
			</ul>

			{/* md (2 columns): column-major reordered so items read top-to-bottom per column */}
			<ul className='relative z-0 hidden gap-x-8 md:grid md:grid-cols-2 lg:hidden'>
				{rows2.map((item, index) =>
					item ? (
						<AStepSelectAmbulanceListItem
							key={item.id}
							item={item}
							onSelect={onRowSelect}
						/>
					) : (
						<li
							key={`ambulance-grid-spacer-2-${index}`}
							aria-hidden
							className='pointer-events-none min-h-0 min-w-0'
						/>
					)
				)}
			</ul>

			{/* lg (3 columns): column-major reordered so items read top-to-bottom per column */}
			<ul className='relative z-0 hidden gap-x-8 lg:grid lg:grid-cols-3'>
				{rows3.map((item, index) =>
					item ? (
						<AStepSelectAmbulanceListItem
							key={item.id}
							item={item}
							onSelect={onRowSelect}
						/>
					) : (
						<li
							key={`ambulance-grid-spacer-3-${index}`}
							aria-hidden
							className='pointer-events-none min-h-0 min-w-0'
						/>
					)
				)}
			</ul>
		</div>
	)
}

export type AStepSelectAmbulanceCategorySectionsProps = {
	/** Active top-level category node (tabs); its `children` are section groups with leaf rows. */
	activeCategoryTree: IAmbulanceCategory
	/** Current search string; trimmed and matched case-insensitively without diacritics against `name` / `nameSingular`. */
	searchQuery: string
	/** Invoked when the user activates a row that has an `ambulance` payload. */
	onRowSelect: (item: IAmbulanceCategory) => void
}

/**
 * For one top-level category: builds search-filtered rows per subgroup and renders a section + {@link AStepSelectAmbulanceGroupGrid} each.
 *
 * @param activeCategoryTree - API tree node whose `children` are grouping headers with nested leaves.
 * @param searchQuery - Free-text filter applied to leaf `name` / `nameSingular` (diacritic-insensitive).
 * @param onRowSelect - Passed through to each grid instance.
 * @returns Fragment of `<section>` blocks (empty groups are omitted).
 */
export function AStepSelectAmbulanceCategorySections({
	activeCategoryTree,
	searchQuery,
	onRowSelect
}: AStepSelectAmbulanceCategorySectionsProps) {
	const visibleGroups = useMemo(() => {
		const q = normalize(searchQuery.trim())
		return activeCategoryTree.children.map(group => {
			const visibleRows = !q
				? group.children
				: group.children.filter(
						item =>
							normalize(item.name).includes(q) ||
							normalize(item.nameSingular).includes(q)
					)
			return { group, visibleRows }
		})
	}, [activeCategoryTree, searchQuery])

	return (
		<>
			{visibleGroups.map(({ group, visibleRows }) => {
				if (!visibleRows.length) return null

				return (
					<section
						key={group.id}
						className='border-medante-purple-dark/20 mb-4 pb-4 md:border-b last:border-b-0 last:pb-0 '
					>
						<h3 className={cn('text-base md:text-xl font-semibold md:font-medium')}>
							{group.name}
						</h3>
						<AStepSelectAmbulanceGroupGrid
							visibleRows={visibleRows}
							onRowSelect={onRowSelect}
						/>
					</section>
				)
			})}
		</>
	)
}
